// src/app/review/page.tsx
'use client'

import { useEffect, useId, useRef, useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { useSession } from 'next-auth/react'
import TutorChat from '@/components/TutorChat'
import MathText from '@/components/MathText'
import { StemRenderer } from '@/components/sat/StemRenderer'
import AccommodationBar from '@/components/sat/AccommodationBar'
import { useAccommodations } from '@/hooks/use-accommodations'
import { useTextZoom } from '@/hooks/use-text-zoom'
import ExplanationView from '@/components/sat/ExplanationView'
import VideoExplanation from '@/components/VideoExplanation'
import { gradeSubmittedAnswer } from '@/lib/test-engine/spr-answer'
import { forwardSessionLimit } from '@/lib/http/session-limit'
import SessionHandoff from '@/components/study-session/SessionHandoff'
import type { BlockOutcome } from '@/lib/study-session/session-plan'
import CalculatorPanel from '@/components/sat/CalculatorPanel'
import * as Sentry from '@sentry/nextjs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import ReportQuestionDialog from '@/components/ReportQuestionDialog'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import SprPrecisionNote from '@/components/sat/SprPrecisionNote'
import SprEntryError from '@/components/sat/SprEntryError'
import { INVALID_ANSWER_FORMAT, practiceAnswerProblem } from '@/lib/sat/answer-validation'
import { ArrowLeft, RotateCcw, CheckCircle2, XCircle, Brain, Zap, Sparkles } from 'lucide-react'
import {
  coerceChoiceToString,
  describeChoiceShape,
  wasChoiceCoerced,
} from '@/lib/test-engine/normalize-choices'
import MissReasonPanel from '@/components/notebook/MissReasonPanel'
import ConfidencePicker, { ConfidenceFeedbackLine } from '@/components/practice/ConfidencePicker'
import { useConfidenceShown, useQuestionConfidence } from '@/components/practice/ConfidencePref'
import { confidenceFeedbackLine } from '@unlimitedtests/core'
import {
  MISS_REASON_LABEL,
  NOTEBOOK_HREF,
  parseNotebookEntry,
  type NotebookEntry,
} from '@/lib/notebook/notebook'

type Question = {
  id: string
  stem: string
  choices: string[]
  answer: string
  topic: string
  explanation: string | null
  /** Structured graphical explanation (jsonb). Validated at render by
   *  parseRichExplanation; left untyped here since the column is untrusted. */
  richExplanation: unknown
  section: 'RW' | 'MATH' | null
  domain: string | null
  itemType: string | null
}

/**
 * What a card is checking (QA finding 10, src/lib/test-engine/review-transfer.ts):
 *   repeat     the missed question again — an ordinary review card
 *   transfer   a question on the same skill never answered before, served right
 *              after a correct repeat. Not a stored card: no SM-2 rating.
 *   retention  another never-seen question on that skill, a few days later,
 *              carried by an ordinary card.
 */
type CardKind = 'repeat' | 'transfer' | 'retention'

type ReviewCard = {
  id: string
  questionId: string
  easeFactor: number
  interval: number
  repetitions: number
  nextReview: string
  question: Question
  kind: CardKind
  /** The skill a transfer or retention card checks. */
  skill?: string
  /** A transfer on a RELATED skill (none left unseen on `skill` itself). */
  related?: string
  /** A transfer card's check, answered through /api/review/transfer/answer. */
  checkId?: string
  /** The student's own mistake-notebook entry for this question (why they
   *  missed it, the rule they saved), or null — src/lib/notebook/notebook.ts. */
  notebook: NotebookEntry | null
}

type Tally = { correct: number; total: number }

/** /api/review/due's `budget` (src/lib/review/review-budget.ts). */
type Budget = {
  perDay: number
  options: number[]
  reviewedToday: number
  minutesPerDay: string
  catchUp: string | null
}

function parseBudget(raw: unknown): Budget | null {
  const b = raw as Partial<Budget> | null | undefined
  if (!b || typeof b.perDay !== 'number' || !Array.isArray(b.options)) return null
  return {
    perDay: b.perDay,
    options: b.options.filter((o): o is number => typeof o === 'number'),
    reviewedToday: typeof b.reviewedToday === 'number' ? b.reviewedToday : 0,
    minutesPerDay: typeof b.minutesPerDay === 'string' ? b.minutesPerDay : '',
    catchUp: typeof b.catchUp === 'string' ? b.catchUp : null,
  }
}

type Difficulty = 'again' | 'hard' | 'good' | 'easy'

/**
 * Normalize + filter a card from /api/review/due. We treat the
 * payload as untrusted because the Question table's `choices` is
 * `Json` in Prisma — anything could be stored there — and
 * malformed render-touched fields crash the whole page.
 *
 * Strategy:
 *   - DROP the card (return null) if the CORE fields are missing
 *     or empty: id, question, question.id, question.stem,
 *     question.answer, and question.choices not being an array.
 *   - COERCE all other render-touched fields to safe shapes:
 *       choices items   →  strings (filter out null/undefined;
 *                          String()-coerce numbers / booleans)
 *       topic           →  string ('' if non-string)
 *       explanation     →  string | null
 *       section         →  'RW' | 'MATH' | null
 *       domain          →  string | null
 *       itemType        →  string | null
 *
 * Why coerce instead of drop: the first defensive pass (PR #471)
 * only checked Array.isArray(choices) but not that the ITEMS were
 * strings. Visakh hit a card where choices contained objects
 * (e.g. {text:'A'} instead of 'A') — the array check passed, the
 * card reached render, and {c} threw "Objects are not valid as
 * React children." Same risk for topic / explanation being
 * objects. Coercing renders the card as best we can instead of
 * showing the user an error page.
 *
 * Anything we drop OR coerce gets a Sentry breadcrumb so the
 * upstream content bug can still be chased.
 */
function normalizeReviewCard(c: unknown): {
  card: ReviewCard | null
  coercedFields: string[]
} {
  const coercedFields: string[] = []
  if (!c || typeof c !== 'object') return { card: null, coercedFields }
  const raw = c as Partial<ReviewCard> & Record<string, unknown>
  if (typeof raw.id !== 'string' || raw.id.length === 0) return { card: null, coercedFields }
  const q = raw.question as Partial<Question> & Record<string, unknown>
  if (!q || typeof q !== 'object') return { card: null, coercedFields }
  if (typeof q.id !== 'string' || q.id.length === 0) return { card: null, coercedFields }
  if (typeof q.stem !== 'string' || q.stem.trim().length === 0) return { card: null, coercedFields }
  if (typeof q.answer !== 'string' || q.answer.trim().length === 0)
    return { card: null, coercedFields }
  if (!Array.isArray(q.choices)) return { card: null, coercedFields }

  // Choices: route through the shared coercion helper. Crucially,
  // for the `{text, label}` shape (the chart-seed / LLM-generation
  // format we hit in production) the helper extracts the `text`
  // field — pre-helper we JSON.stringify'd it and the user saw
  // `{"text":"5","label":"A"}` instead of `5`. Visakh report:
  // "look at the review card that finally came. why are the answer
  // choices looking so odd."
  //
  // Track WHICH coercion happened so the Sentry "payload health"
  // breadcrumb still names the upstream content shape worth
  // chasing at the data layer.
  const normalizedChoices: string[] = []
  for (const item of q.choices) {
    if (wasChoiceCoerced(item)) {
      coercedFields.push(`choices[${describeChoiceShape(item)}]`)
    }
    normalizedChoices.push(coerceChoiceToString(item))
  }

  const safeTopic = typeof q.topic === 'string' ? q.topic : ''
  if (q.topic != null && typeof q.topic !== 'string') coercedFields.push('topic')

  const safeExplanation =
    typeof q.explanation === 'string' ? q.explanation : q.explanation == null ? null : null
  if (q.explanation != null && typeof q.explanation !== 'string') coercedFields.push('explanation')

  const safeSection = q.section === 'RW' || q.section === 'MATH' ? q.section : null
  if (q.section != null && q.section !== 'RW' && q.section !== 'MATH') coercedFields.push('section')

  const safeDomain = typeof q.domain === 'string' ? q.domain : null
  if (q.domain != null && typeof q.domain !== 'string') coercedFields.push('domain')

  const safeItemType = typeof q.itemType === 'string' ? q.itemType : null
  if (q.itemType != null && typeof q.itemType !== 'string') coercedFields.push('itemType')

  const card: ReviewCard = {
    id: raw.id,
    questionId: typeof raw.questionId === 'string' ? raw.questionId : q.id,
    easeFactor: typeof raw.easeFactor === 'number' ? raw.easeFactor : 2.5,
    interval: typeof raw.interval === 'number' ? raw.interval : 1,
    repetitions: typeof raw.repetitions === 'number' ? raw.repetitions : 0,
    nextReview: typeof raw.nextReview === 'string' ? raw.nextReview : new Date().toISOString(),
    ...retentionOf(raw.check),
    notebook: parseNotebookEntry(raw.notebook),
    question: {
      id: q.id,
      stem: q.stem,
      choices: normalizedChoices,
      answer: q.answer,
      topic: safeTopic,
      explanation: safeExplanation,
      // Passed through untouched — parseRichExplanation validates the shape at
      // render and falls back to `explanation` when it's absent/malformed.
      richExplanation: q.richExplanation ?? null,
      section: safeSection,
      domain: safeDomain,
      itemType: safeItemType,
    },
  }

  return { card, coercedFields }
}

/** A due card's `check` from /api/review/due: a retention card, or a plain repeat. */
function retentionOf(check: unknown): Pick<ReviewCard, 'kind' | 'skill'> {
  const c = check as { kind?: unknown; skill?: unknown } | null | undefined
  return c && c.kind === 'RETENTION' && typeof c.skill === 'string'
    ? { kind: 'retention', skill: c.skill }
    : { kind: 'repeat' }
}

const CARD_TITLE: Record<CardKind, string> = {
  repeat: 'Review Card',
  transfer: 'New question, same skill',
  retention: 'Skill check',
}

/**
 * Self-rating buttons shown only after a CORRECT answer. The user
 * already proved they knew it; the rating refines SM-2's interval
 * (3 = harder than expected → shorter next interval; 5 = trivial
 * → longer next interval).
 *
 * Pre-fix this list also included "Again" — but with self-rating
 * only, a dishonest user could mark a missed question as "Easy"
 * and skip the spaced-repetition benefit entirely. The new flow
 * grades objectively first: a wrong answer auto-submits "again"
 * (quality 0) without giving the user the choice. The escape
 * hatch for "I genuinely don't know" is a separate "Skip — I
 * don't know" button on the question, which maps to the same
 * "again" outcome.
 */
const CORRECT_RATING_BUTTONS: {
  key: Exclude<Difficulty, 'again'>
  label: string
  color: string
  icon: React.ReactNode
}[] = [
  {
    key: 'hard',
    label: 'Hard',
    color:
      'border-orange-500/60 bg-orange-500/10 text-orange-700 dark:text-orange-300 hover:bg-orange-500/20',
    icon: <Brain className="h-4 w-4" />,
  },
  {
    key: 'good',
    label: 'Good',
    color: 'border-brand/60 bg-brand/10 text-brand-ink hover:bg-brand/20',
    icon: <Zap className="h-4 w-4" />,
  },
  {
    key: 'easy',
    label: 'Easy',
    color:
      'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20',
    icon: <Sparkles className="h-4 w-4" />,
  },
]

export default function ReviewPage() {
  const router = useRouter()
  const { data: sessionData } = useSession()
  // Gates the AI tutor on review cards (same PREMIUM gate as practice).
  const plan = ((sessionData?.user as { plan?: string } | undefined)?.plan ?? 'FREE') as string
  // Accommodations apply on EVERY surface where a student reads a question,
  // not only where they are timed. /review was the one question surface that
  // never mounted the bar, so a student with large print or the reader enabled
  // silently lost both here — the same settings that work in practice and on a
  // full test just stopped applying.
  const { accommodations } = useAccommodations()
  const { level: textZoom, setLevel: setTextZoom } = useTextZoom(accommodations.largePrint)
  const readingRootRef = useRef<HTMLDivElement>(null)

  const [cards, setCards] = useState<ReviewCard[]>([])
  const [totalDue, setTotalDue] = useState(0)
  // The daily review budget and catch-up line from /api/review/due
  // (review-budget.ts). Null until loaded, or from an older server.
  const [budget, setBudget] = useState<Budget | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentIdx, setCurrentIdx] = useState(0)
  // Per-card interaction state.
  //   phase = 'idle'     → user is reading the question, hasn't committed
  //   phase = 'answered' → user has committed; we're showing graded result
  // userAnswer = what the user picked / typed (letter for MCQ, string for SPR)
  //              OR null if they used the "Skip — I don't know" escape hatch
  // isCorrect  = derived from userAnswer + question.answer at commit time
  const [phase, setPhase] = useState<'idle' | 'answered'>('idle')
  const [userAnswer, setUserAnswer] = useState<string | null>(null)
  // Optional "How sure are you?" (src/lib/practice/confidence.ts). Picking an
  // MCQ choice commits it here, so the control sits ABOVE the choices: rate,
  // then pick. Keyed by card, so the next card starts unrated.
  const confidenceOffered = useConfidenceShown()
  const [confidence, setConfidence] = useQuestionConfidence(cards[currentIdx]?.id)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  // SPR-only: controlled input value before commit. Cleared on advance.
  const [sprInput, setSprInput] = useState('')
  // An unreadable grid-in entry ("abc") is an input error, shown inline and
  // never committed — it used to be graded wrong and counted as a miss. Keyed
  // by card so advancing clears it.
  const [formatError, setFormatError] = useState<{ cardId: string; message: string } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [completed, setCompleted] = useState(0)
  const [sessionDone, setSessionDone] = useState(false)
  // This session's answers, repeats kept apart from never-seen questions
  // (transfer + retention): the summary shows both, and only the second shows
  // the student can use the rule on a question they haven't seen.
  const [tally, setTally] = useState<{ repeat: Tally; fresh: Tally }>({
    repeat: { correct: 0, total: 0 },
    fresh: { correct: 0, total: 0 },
  })
  // A one-line note carried onto the next card ("we'll check this again on…").
  const [notice, setNotice] = useState<string | null>(null)
  const [savingBudget, setSavingBudget] = useState(false)
  // New questions served this session. They aren't stored cards, so they come
  // off `completed` before it is compared with the cards still due.
  const [transfersDone, setTransfersDone] = useState(0)
  const cardsDone = completed - transfersDone

  // A time-boxed session links here with ?limit=N, so the FIRST batch is the
  // number of cards its plan promised. "Continue Reviewing" is past that plan
  // and gets the normal batch.
  // `more`: "review more anyway", past today's budget.
  const fetchDueCards = useCallback(async (withSessionLimit = false, more = false) => {
    setLoading(true)
    setError(null)
    try {
      const limit = withSessionLimit ? forwardSessionLimit(window.location.search) : ''
      const query = limit || (more ? '?more=1' : '')
      const res = await fetch(`/api/review/due${query}`, { cache: 'no-store' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data.error ?? `Failed to load cards (${res.status})`)
      }
      const data = await res.json()

      // Boundary defense for the /api/review/due payload. Two
      // failure modes we have to handle:
      //
      //  (1) DROP cards where CORE fields are missing/empty
      //      (id, question, question.id/.stem/.answer, choices not
      //      an array). Pre-fix, `question.answer.trim()` threw on
      //      the very first render for these.
      //
      //  (2) COERCE render-touched fields to safe shapes. Pre-fix,
      //      we only ran (1) and trusted the contents. But the
      //      Question.choices column is Prisma `Json` — anything
      //      could be in there. Visakh hit a card where choices
      //      contained OBJECTS, not strings; the array check
      //      passed, the card reached render, then {c} threw
      //      "Objects are not valid as React children" and the
      //      page bumped up to /review/error.tsx. Same risk for
      //      topic / explanation being non-string. Coercing
      //      renders the card as best we can rather than dumping
      //      the user on an error page.
      //
      // Both DROP and COERCE log to Sentry so the upstream
      // content bug can be chased without breaking the user's
      // session.
      const incoming: unknown[] = Array.isArray(data?.cards) ? data.cards : []
      const valid: ReviewCard[] = []
      const droppedIds: string[] = []
      const coerced: Array<{ id: string; fields: string[] }> = []
      for (const c of incoming) {
        const { card, coercedFields } = normalizeReviewCard(c)
        if (card) {
          valid.push(card)
          if (coercedFields.length > 0) {
            coerced.push({ id: card.id, fields: coercedFields })
          }
        } else {
          // c is unknown; pull an id off it best-effort for logging.
          const maybeId = (c as { id?: unknown })?.id
          droppedIds.push(typeof maybeId === 'string' ? maybeId : '<unknown>')
        }
      }
      if (droppedIds.length > 0 || coerced.length > 0) {
        // Wrap Sentry in its own try/catch — if the SDK is
        // misbehaving in this client, a thrown event call would
        // otherwise tank the page and we'd never see the actual
        // upstream data bug.
        try {
          Sentry.captureMessage(
            `[review] payload health: dropped ${droppedIds.length}, coerced ${coerced.length} of ${incoming.length} from /api/review/due`,
            { level: 'warning', extra: { droppedIds, coerced } },
          )
        } catch {
          // Best-effort: swallow Sentry failures. The user still
          // gets a working session.
        }
      }

      setCards(valid)
      setTotalDue(typeof data?.totalDue === 'number' ? data.totalDue : valid.length)
      setBudget(parseBudget(data?.budget))
      setCurrentIdx(0)
      setPhase('idle')
      setUserAnswer(null)
      setIsCorrect(null)
      setSprInput('')
      setCompleted(0)
      // "N cards reviewed", the cards-due line and the tally all describe this
      // batch, so they start over together.
      setTransfersDone(0)
      setTally({ repeat: { correct: 0, total: 0 }, fresh: { correct: 0, total: 0 } })
      setSessionDone(valid.length === 0)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load review cards')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDueCards(true)
  }, [fetchDueCards])

  /** Save a new daily budget, then reload: a bigger one may offer cards now. */
  async function changeBudget(perDay: number) {
    if (savingBudget || perDay === budget?.perDay) return
    setSavingBudget(true)
    try {
      const res = await fetch('/api/review/budget', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ perDay }),
        cache: 'no-store',
      })
      if (res.ok) await fetchDueCards()
    } finally {
      setSavingBudget(false)
    }
  }

  // Scroll back to the top whenever the card advances. A long previous card
  // can leave the page scrolled down, so the next card would otherwise open
  // mid-page. `currentIdx` is the single value that changes on every card
  // transition (and resets to 0 on load / "Continue Reviewing").
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0 })
    }
  }, [currentIdx])

  /**
   * Grade a user-committed answer against the question's correct answer.
   * Client-side (question.answer ships with the card), but it runs the SAME
   * shared grader as every server route — lib/test-engine/spr-answer.
   *
   * It previously did `trim().toUpperCase() ===` for BOTH types, described in
   * a comment as working for SPR too. It does not: the review queue has no
   * itemType filter, so grid-in cards reach this surface and "1.8" was graded
   * wrong against a stored "1.80".
   */
  function gradeAnswer(question: Question, answer: string): boolean {
    return gradeSubmittedAnswer(question.answer, question.itemType, answer)
  }

  /**
   * Commit the user's answer for the current card. Transitions
   * phase: 'idle' → 'answered'. The result panel (correct/wrong
   * card + explanation) renders next.
   *
   * What DOESN'T happen here: the SM-2 submit itself. That fires
   * when the user clicks a rating button (correct path) or the
   * "Next card" button (wrong path). Reason: submitReview()
   * advances to the next card, so calling it from here would
   * skip past the explanation panel — the student would never
   * see what they got wrong. Both paths route through the same
   * submitReview() so the SM-2 quality wiring stays in one place.
   */
  function commitAnswer(answer: string | null) {
    if (phase !== 'idle' || !cards[currentIdx]) return
    const q = cards[currentIdx].question
    const correct = answer !== null && gradeAnswer(q, answer)
    // "Skip — I don't know" is its own answer, not a rated one.
    if (answer === null && confidence) setConfidence(null)
    setUserAnswer(answer)
    setIsCorrect(correct)
    setPhase('answered')
  }

  /**
   * The server refused the committed entry as unreadable (the same rule the
   * entry box applies, so only an older bundle or a rule change lands here).
   * Undo the local commit: back to the input, entry kept, error shown.
   */
  function reopenForFormatError(cardId: string, message: string) {
    setPhase('idle')
    setUserAnswer(null)
    setIsCorrect(null)
    setFormatError({ cardId, message })
  }

  /** Move past the current card; `list` is the queue to move through. */
  function advance(list: ReviewCard[]) {
    setCompleted((n) => n + 1)
    if (currentIdx + 1 >= list.length) {
      setSessionDone(true)
    } else {
      setCurrentIdx((i) => i + 1)
      // Reset per-card state for the next card.
      setPhase('idle')
      setUserAnswer(null)
      setIsCorrect(null)
      setSprInput('')
    }
  }

  function count(kind: 'repeat' | 'fresh', correct: boolean) {
    setTally((t) => ({
      ...t,
      [kind]: { correct: t[kind].correct + (correct ? 1 : 0), total: t[kind].total + 1 },
    }))
  }

  /**
   * After a CORRECT repeat, ask for a question on the same skill the student has
   * never answered, and put it next in the queue. Best-effort: if it fails, the
   * session just carries on.
   */
  async function nextWithTransfer(card: ReviewCard): Promise<ReviewCard[]> {
    try {
      const res = await fetch('/api/review/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId: card.id }),
        cache: 'no-store',
      })
      if (!res.ok) return cards
      const data = await res.json()
      if (data?.status === 'none' && typeof data.skill === 'string') {
        setNotice(`No new questions left on ${data.skill} to check it with.`)
        return cards
      }
      if (data?.status !== 'started') return cards
      const { card: fresh } = normalizeReviewCard({
        id: `transfer:${data.checkId}`,
        questionId: data.question?.id,
        question: data.question,
      })
      if (!fresh) return cards
      const transfer: ReviewCard = {
        ...fresh,
        kind: 'transfer',
        skill: data.skill,
        ...(typeof data.related === 'string' && data.related ? { related: data.related } : {}),
        checkId: data.checkId,
      }
      const next = [...cards.slice(0, currentIdx + 1), transfer, ...cards.slice(currentIdx + 1)]
      setCards(next)
      return next
    } catch {
      return cards
    }
  }

  async function submitReview(difficulty: Difficulty) {
    const card = cards[currentIdx]
    if (submitting || !card) return
    setSubmitting(true)
    setNotice(null)

    try {
      const res = await fetch('/api/review/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cardId: card.id,
          difficulty,
          // Graded on the server and kept as a repeat or a skill check.
          response: userAnswer,
          ...(confidenceOffered && confidence && userAnswer !== null ? { confidence } : {}),
        }),
        cache: 'no-store',
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (res.status === 400 && data?.code === INVALID_ANSWER_FORMAT) {
          reopenForFormatError(card.id, data.error)
          return
        }
        throw new Error(data.error ?? 'Failed to submit review')
      }

      // A skipped skill check is "not attempted", not a miss: leave it uncounted.
      if (card.kind === 'repeat') count('repeat', isCorrect === true)
      else if (userAnswer !== null) count('fresh', isCorrect === true)

      const list =
        card.kind === 'repeat' && isCorrect === true ? await nextWithTransfer(card) : cards
      advance(list)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit review')
    } finally {
      setSubmitting(false)
    }
  }

  /** Grade a transfer question; the server schedules the later skill check. */
  async function submitTransfer() {
    const card = cards[currentIdx]
    if (submitting || !card?.checkId) return
    setSubmitting(true)
    setNotice(null)
    try {
      const res = await fetch('/api/review/transfer/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          checkId: card.checkId,
          response: userAnswer,
          ...(confidenceOffered && confidence && userAnswer !== null ? { confidence } : {}),
        }),
        cache: 'no-store',
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        if (res.status === 400 && data?.code === INVALID_ANSWER_FORMAT) {
          reopenForFormatError(card.id, data.error)
          return
        }
        throw new Error(data.error ?? 'Failed to save your answer')
      }
      const data = await res.json()
      count('fresh', data?.correct === true)
      setTransfersDone((n) => n + 1)
      if (typeof data?.retentionDueAt === 'string') {
        const when = new Date(data.retentionDueAt).toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        })
        setNotice(
          `We'll check ${card.skill ?? 'this skill'} again with another new question on ${when}.`,
        )
      }
      advance(cards)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save your answer')
    } finally {
      setSubmitting(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-2xl px-4 py-8">
          <p className="text-muted-foreground">Loading review cards...</p>
        </div>
      </main>
    )
  }

  // Error state
  if (error) {
    return (
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-2xl px-4 py-8">
          <div className="rounded-md border border-red-800 bg-red-100 dark:bg-red-950/60 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
          <Button
            variant="outline"
            className="mt-4 border-border text-muted-foreground"
            onClick={() => router.push('/dashboard')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </main>
    )
  }

  // Today's review budget is spent and cards are still due: say so, show the
  // catch-up schedule, and keep "more" one click away.
  const reviewedTodayNow = (budget?.reviewedToday ?? 0) + cardsDone
  const stillDue = Math.max(0, totalDue - cardsDone)
  const budgetSpent = budget != null && stillDue > 0 && reviewedTodayNow >= budget.perDay
  // What this page did, for a time-boxed session's hand-off (SessionHandoff
  // renders nothing outside a session): the cards reviewed and how many right.
  const sessionOutcome: BlockOutcome =
    completed > 0
      ? {
          status: 'done',
          answered: completed,
          correct: tally.repeat.correct + tally.fresh.correct,
        }
      : { status: 'nothing-left' }
  if (sessionDone && budgetSpent && budget) {
    return (
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-2xl px-4 py-8">
          <Card className="border-border/70 bg-card/60" data-testid="budget-done">
            <CardHeader>
              <CardTitle className="text-xl text-foreground">Today&apos;s review is done</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="text-center">
                <CheckCircle2 className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" />
                <p className="mt-4 text-lg font-semibold">
                  {reviewedTodayNow} {reviewedTodayNow === 1 ? 'card' : 'cards'} reviewed today
                </p>
                <TallyLine tally={tally} />
                <p className="mt-1 text-sm text-muted-foreground">
                  {stillDue} still due. Your daily review is {budget.perDay} cards.
                </p>
              </div>
              <BudgetBar budget={budget} onChange={changeBudget} saving={savingBudget} />
              <SessionHandoff outcome={sessionOutcome} />
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-border text-muted-foreground"
                  onClick={() => router.push('/dashboard')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
                <Button
                  className="flex-1 bg-brand text-on-brand"
                  onClick={() => fetchDueCards(false, true)}
                >
                  Review {Math.min(20, stillDue)} more anyway
                </Button>
              </div>
              <NotebookLink />
            </CardContent>
          </Card>
        </div>
      </main>
    )
  }

  // Session complete or no cards due
  if (sessionDone) {
    return (
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-2xl px-4 py-8">
          <Card className="border-border/70 bg-card/60">
            <CardHeader>
              <CardTitle className="text-xl text-foreground">
                {completed > 0 ? 'Review Session Complete' : 'All Caught Up!'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {completed > 0 ? (
                <div className="text-center">
                  <CheckCircle2 className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" />
                  <p className="mt-4 text-2xl font-bold">{completed} cards reviewed</p>
                  <TallyLine tally={tally} />
                  <p className="mt-1 text-sm text-muted-foreground">
                    {totalDue - cardsDone > 0
                      ? `${totalDue - cardsDone} more cards due later today`
                      : 'No more cards due right now'}
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <Sparkles className="mx-auto h-16 w-16 text-brand-ink" />
                  <p className="mt-4 text-lg text-muted-foreground">
                    No cards are due for review right now.
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Take a practice test or come back later when cards are due.
                  </p>
                </div>
              )}

              {budget && budget.catchUp && (
                <BudgetBar budget={budget} onChange={changeBudget} saving={savingBudget} />
              )}

              <SessionHandoff outcome={sessionOutcome} />

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-border text-muted-foreground"
                  onClick={() => router.push('/dashboard')}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Dashboard
                </Button>
                {totalDue - cardsDone > 0 && (
                  <Button className="flex-1 bg-brand text-on-brand" onClick={() => fetchDueCards()}>
                    Continue Reviewing
                  </Button>
                )}
              </div>
              <NotebookLink />
            </CardContent>
          </Card>
        </div>
      </main>
    )
  }

  // Active review
  //
  // Defensive: if currentIdx is somehow out of range AND none of the
  // earlier early-returns fired (loading / error / sessionDone), we
  // would crash on `card.question` below. This is a hatch for any
  // state-update race I couldn't pin down: Visakh hit a
  // "Something went wrong" error boundary after clicking Next card,
  // and the most plausible cause is currentIdx pointing past the
  // array between submitReview's setSessionDone and the next render.
  //
  // Falling through to setSessionDone (via an effect would loop) is
  // tricky, so the simplest safe move is to render the session-done
  // view inline. The user sees "Session Complete" instead of an
  // error boundary — graceful degradation.
  const card = cards[currentIdx]
  if (!card) {
    return (
      <main className="min-h-dvh bg-background text-foreground">
        <div className="mx-auto max-w-2xl px-4 py-8">
          <Card className="border-border/70 bg-card/60">
            <CardHeader>
              <CardTitle className="text-xl text-foreground">
                {completed > 0 ? 'Review Session Complete' : 'All Caught Up!'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <CheckCircle2 className="mx-auto h-16 w-16 text-green-600 dark:text-green-400" />
              {completed > 0 && <p className="text-2xl font-bold">{completed} cards reviewed</p>}
              <Button
                variant="outline"
                className="border-border text-muted-foreground"
                onClick={() => router.push('/dashboard')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    )
  }
  const question = card.question
  const choices = (question.choices as string[]) ?? []
  const progressPct = cards.length > 0 ? Math.round((completed / cards.length) * 100) : 0

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-2xl px-4 py-8">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => router.push('/dashboard')}
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <Badge variant="outline" className="border-border text-muted-foreground">
              {/* The session is capped at REVIEW_SESSION_CAP, so totalDue (all
                  due) can exceed the loaded batch. Label both numbers explicitly
                  so "this batch of N" and "M due today" don't read as a mismatch
                  against the "Card x of N" progress — the rest come in a later
                  batch today (the session-complete screen says how many). */}
              {cards.length < totalDue
                ? `${totalDue} due today · batch of ${cards.length}`
                : `${totalDue} due today`}
            </Badge>
          </div>
          <div className="w-48">
            <div className="mb-1 text-right text-xs text-muted-foreground">
              Card {completed + 1} of {cards.length}
            </div>
            <Progress value={progressPct} />
          </div>
        </div>

        {budget?.catchUp && currentIdx === 0 && phase === 'idle' && (
          <p data-testid="review-catch-up-active" className="mb-3 text-sm text-muted-foreground">
            Today: {budget.perDay} cards, {budget.minutesPerDay}. {budget.catchUp}
          </p>
        )}
        {notice && (
          <p
            data-testid="review-notice"
            className="mb-3 rounded-md border border-border/70 bg-card/60 px-3 py-2 text-sm text-muted-foreground"
          >
            {notice}
          </p>
        )}

        {/* Card */}
        <Card className="border-border/70 bg-card/60">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base text-foreground">
                {card.kind === 'transfer' && card.related
                  ? 'New question, related skill'
                  : CARD_TITLE[card.kind]}
              </CardTitle>
              {/* Flag a bad question straight from review (founder ask 2026-07-06) */}
              <span className="ml-auto">
                <ReportQuestionDialog questionId={card.questionId} />
              </span>
              {question.section && (
                <Badge
                  variant="outline"
                  className={
                    question.section === 'RW'
                      ? 'border-brand/40 text-brand-ink'
                      : 'border-brand/40 text-brand-ink'
                  }
                >
                  {question.section === 'RW' ? 'R&W' : 'Math'}
                </Badge>
              )}
              {question.topic && (
                <Badge variant="outline" className="border-border text-muted-foreground">
                  {question.topic}
                </Badge>
              )}
            </div>
            {card.kind !== 'repeat' && (
              <p data-testid="review-card-why" className="mt-1 text-sm text-muted-foreground">
                {card.kind === 'transfer' && card.related
                  ? `You got that one right. You've seen every ${card.skill ?? 'same-skill'} question, so this new one is on ${card.related}, which tests the same idea.`
                  : card.kind === 'transfer'
                    ? `You got that one right. Now one you've never seen, on ${card.skill ?? 'the same skill'}.`
                    : `A question you've never seen, on ${card.skill ?? 'a skill you reviewed'}, to check it stuck.`}
              </p>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            <AccommodationBar
              accommodations={accommodations}
              zoomLevel={textZoom}
              onZoomChange={setTextZoom}
              readingRootRef={readingRootRef}
              questionKey={question.id}
            />
            {/* Question stem */}
            <div
              ref={readingRootRef}
              style={{ zoom: textZoom }}
              className="text-lg leading-relaxed text-foreground"
            >
              <StemRenderer stem={question.stem} className="text-lg leading-relaxed" />
            </div>

            {/* Optional confidence rating, before the answer is committed. */}
            {confidenceOffered && phase === 'idle' && (
              <ConfidencePicker value={confidence} onChange={setConfidence} />
            )}

            {/* Answer entry — interactive in 'idle' phase, locked + graded
                visual in 'answered' phase. MCQ uses clickable choice
                buttons; SPR uses a numeric input. Pre-fix this whole
                section was display-only (just shows choices, no
                interaction) which let students self-deceive on whether
                they actually knew the answer. New flow forces a commit. */}
            {question.itemType === 'SPR' || choices.length === 0 ? (
              <SprAnswerEntry
                phase={phase}
                userAnswer={userAnswer}
                isCorrect={isCorrect}
                sprInput={sprInput}
                onSprInputChange={(v) => {
                  setSprInput(v)
                  setFormatError(null)
                }}
                formatError={formatError?.cardId === card.id ? formatError.message : null}
                onCommit={(ans) => {
                  // Same format rule the server enforces (lib/sat/answer-validation).
                  const problem = practiceAnswerProblem(question.itemType, ans)
                  if (problem) {
                    setFormatError({ cardId: card.id, message: problem })
                    return
                  }
                  setFormatError(null)
                  commitAnswer(ans)
                }}
              />
            ) : (
              <McqAnswerEntry
                choices={choices}
                correctLetter={question.answer.trim().toUpperCase()}
                phase={phase}
                userAnswer={userAnswer}
                onPick={(letter) => commitAnswer(letter)}
              />
            )}

            {/* Inline Desmos calculator on Math cards — same one the digital
                SAT provides. Gated per-card (a review session mixes R&W + Math
                cards), so it never shows on R&W. */}
            {question.section === 'MATH' && <CalculatorPanel className="pt-1" />}

            {/* Skip button — only meaningful while in 'idle' phase. Maps
                to a wrong answer (quality 0). The escape hatch is
                deliberate so students who genuinely don't know aren't
                forced to type a random guess. */}
            {phase === 'idle' && (
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => commitAnswer(null)}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                >
                  Skip — I don&apos;t know
                </button>
              </div>
            )}

            {/* Result panel — shown after the user commits. Green +
                contrasted text on correct; red on wrong. Pre-fix the
                explanation used text-muted-foreground inside a dark
                green card → unreadable (Visakh flagged). Fixed with
                emerald/rose-tinted text in the same hue family as the
                card, with proper light+dark variants. */}
            {phase === 'answered' && (
              <div className="space-y-4">
                {isCorrect ? (
                  <div className="rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-emerald-900">
                      <CheckCircle2 className="h-4 w-4" aria-hidden />
                      Correct — answer is {question.answer}
                    </div>
                    <ConfidenceFeedbackLine
                      line={confidenceFeedbackLine(confidence, true)}
                      className="text-emerald-900"
                    />
                    {(question.richExplanation || question.explanation) && (
                      <div className="mt-2 leading-relaxed text-emerald-900/90">
                        <ExplanationView
                          rich={question.richExplanation}
                          explanation={question.explanation}
                          studentPick={userAnswer}
                          isCorrect={isCorrect}
                          choices={question.choices}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-md border border-rose-300 bg-rose-50 px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 font-semibold text-rose-900">
                      <XCircle className="h-4 w-4" aria-hidden />
                      {userAnswer === null ? 'Skipped' : 'Incorrect'} — correct answer is{' '}
                      {question.answer}
                    </div>
                    <SprPrecisionNote
                      entry={userAnswer}
                      answerKey={question.answer}
                      className="text-rose-900"
                    />
                    <ConfidenceFeedbackLine
                      line={confidenceFeedbackLine(confidence, false)}
                      className="text-rose-900"
                    />
                    {(question.richExplanation || question.explanation) && (
                      <div className="mt-2 leading-relaxed text-rose-900/90">
                        <ExplanationView
                          rich={question.richExplanation}
                          explanation={question.explanation}
                          studentPick={userAnswer}
                          isCorrect={isCorrect}
                          choices={question.choices}
                        />
                        {isCorrect === false && question.topic && (
                          <VideoExplanation topic={question.topic} />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* The student's own rule for this question, from their mistake
                    notebook — the reminder they wrote last time they missed it. */}
                {card.notebook?.rule && (
                  <div
                    data-testid="review-own-rule"
                    className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
                  >
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                      Your rule
                      {card.notebook.missReason
                        ? ` · last time: ${MISS_REASON_LABEL[card.notebook.missReason].toLowerCase()}`
                        : ''}
                    </div>
                    <p className="mt-1">{card.notebook.rule}</p>
                  </div>
                )}

                {/* A miss: why, in one tap, plus a rule to remember. Saves the
                    question to the mistake notebook. */}
                {!isCorrect && (
                  <MissReasonPanel
                    key={`notebook:${card.id}`}
                    questionId={card.questionId}
                    initial={card.notebook}
                    surface="paper"
                    showNotebookLink={false}
                    confidence={confidence}
                  />
                )}

                {/* AI tutor on the review card — same component + PREMIUM
                    gate as practice. The student just saw the graded result;
                    the tutor opens click-to-explain with their answer +
                    correctness as context. */}
                <TutorChat
                  key={card.id}
                  questionId={card.questionId}
                  questionStem={question.stem}
                  correctAnswer={question.answer}
                  isCorrect={isCorrect ?? undefined}
                  userAnswer={userAnswer ?? undefined}
                  plan={plan}
                />

                {/* Rating buttons — only shown on correct. A wrong
                    answer was already auto-submitted as 'again' from
                    commitAnswer(); we just show a Continue button
                    that advances. */}
                {card.kind === 'transfer' ? (
                  // A never-seen question is not a stored card: no SM-2 rating.
                  // A miss becomes a review card on the server.
                  <div className="flex justify-center">
                    <Button
                      className="bg-brand text-on-brand"
                      disabled={submitting}
                      onClick={() => submitTransfer()}
                    >
                      {currentIdx + 1 >= cards.length ? 'Finish session' : 'Continue'}
                    </Button>
                  </div>
                ) : isCorrect ? (
                  <div>
                    <p className="mb-2 text-center text-sm text-muted-foreground">
                      How well did you know this?
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {CORRECT_RATING_BUTTONS.map((btn) => (
                        <button
                          key={btn.key}
                          onClick={() => submitReview(btn.key)}
                          // data-answer-cell: keep the rating buttons' own
                          // colors while submitting (global disabled guard).
                          data-answer-cell
                          disabled={submitting}
                          className={`flex flex-col items-center gap-1 rounded-lg border px-3 py-3 text-sm font-medium transition ${btn.color} disabled:opacity-50`}
                        >
                          {btn.icon}
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Wrong / skipped: submitReview('again') already
                  // fired from commitAnswer(). This button just
                  // advances to the next card.
                  <div className="flex justify-center">
                    <Button
                      className="bg-brand text-on-brand"
                      disabled={submitting}
                      onClick={() => submitReview('again')}
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      {currentIdx + 1 >= cards.length ? 'Finish session' : 'Next card'}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}

/** Where the reasons and rules saved from review cards collect. */
function NotebookLink() {
  return (
    <p className="text-center text-sm text-muted-foreground">
      <Link href={NOTEBOOK_HREF} className="font-medium text-brand-ink hover:underline">
        Open your mistake notebook
      </Link>{' '}
      to see why you missed questions and the rules you saved.
    </p>
  )
}

/**
 * The daily review budget and the catch-up schedule (QA: 114 due cards and no
 * route through them). Picking a budget saves it and reloads.
 */
function BudgetBar({
  budget,
  onChange,
  saving,
}: {
  budget: Budget
  onChange: (perDay: number) => void
  saving: boolean
}) {
  return (
    <div
      data-testid="review-budget"
      className="rounded-md border border-border/70 bg-card/40 p-3 text-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          Daily review: <span className="font-medium">{budget.perDay} cards</span>
          {budget.minutesPerDay ? (
            <span className="text-muted-foreground"> · {budget.minutesPerDay} a day</span>
          ) : null}
        </span>
        <div role="group" aria-label="Cards a day" className="flex gap-1">
          {budget.options.map((o) => (
            <button
              key={o}
              type="button"
              aria-pressed={o === budget.perDay}
              disabled={saving}
              onClick={() => onChange(o)}
              className={`rounded-md border px-2.5 py-1 text-xs ${
                o === budget.perDay
                  ? 'border-brand bg-brand/10 font-semibold text-brand-ink'
                  : 'border-border text-muted-foreground hover:text-foreground'
              } disabled:opacity-50`}
            >
              {o}
            </button>
          ))}
        </div>
      </div>
      {budget.catchUp && (
        <p data-testid="review-catch-up" className="mt-1.5 text-muted-foreground">
          {budget.catchUp}
        </p>
      )}
    </div>
  )
}

/**
 * Repeats and never-seen questions, counted apart: a right repeat can be memory,
 * a right new question shows the rule carried over.
 */
function TallyLine({ tally }: { tally: { repeat: Tally; fresh: Tally } }) {
  if (tally.repeat.total === 0 && tally.fresh.total === 0) return null
  const part = (t: Tally, what: string) => `${what}: ${t.correct} of ${t.total} right`
  return (
    <p data-testid="review-tally" className="mt-2 text-sm text-muted-foreground">
      {[
        tally.repeat.total > 0 ? part(tally.repeat, 'Repeats') : null,
        tally.fresh.total > 0 ? part(tally.fresh, 'New questions') : null,
      ]
        .filter(Boolean)
        .join(' · ')}
    </p>
  )
}

/* ------------------------------------------------------------------ */
/*  Answer-entry sub-components                                       */
/*                                                                    */
/*  Split out from the main component so the per-card answer flow is  */
/*  testable in isolation and the main file's render logic stays      */
/*  scannable. Both are intentionally dumb — all state lives in the   */
/*  parent (ReviewPage); these just render and call back.             */
/* ------------------------------------------------------------------ */

interface McqAnswerEntryProps {
  choices: string[]
  /** Uppercased single letter — the correct choice key (A/B/C/D). */
  correctLetter: string
  phase: 'idle' | 'answered'
  /** The letter the user picked, or null if no pick yet. */
  userAnswer: string | null
  onPick: (letter: string) => void
}

/**
 * MCQ answer entry. Each choice is a clickable button while
 * phase === 'idle'. After commit, the buttons lock and we apply
 * correctness coloring:
 *   - The CORRECT choice always gets the green outline (so the
 *     student sees what they should have picked, whether they
 *     got it right or not).
 *   - The user's WRONG pick gets the red outline.
 */
function McqAnswerEntry({
  choices,
  correctLetter,
  phase,
  userAnswer,
  onPick,
}: McqAnswerEntryProps) {
  return (
    <div className="grid gap-2">
      {choices.map((c, i) => {
        const letter = ['A', 'B', 'C', 'D'][i] ?? String.fromCharCode(65 + i)
        const isCorrectChoice = letter === correctLetter
        const isUserPick = userAnswer === letter

        // FIXED LIGHT "exam paper" colors — NO bg-card / theme tokens / dark:
        // variants. The review card is an answer-review surface like the SAT
        // Runner + SectionReviewPage, which all render as light paper regardless
        // of the app theme. The cells used to follow the theme (bg-card +
        // dark:), so in any dark-mode state they turned navy on the
        // already-force-light stem — the recurring "dark cells are back" bug.
        // Pinning explicit slate/green/red light values makes that impossible.
        let className =
          'flex items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition '
        if (phase === 'idle') {
          // Dark slate text AT REST (readable on the light slate-50 cell); flip to
          // on-brand (white) only on hover, when the bg becomes emerald. The old
          // `text-on-brand` alone was white-on-near-white in light mode — the
          // choices were invisible until you hovered (founder report 2026-07-14).
          className +=
            'border border-slate-300 bg-slate-50 text-slate-800 hover:border-brand hover:bg-brand-hover hover:text-on-brand cursor-pointer'
        } else if (isCorrectChoice) {
          className +=
            'border-2 border-green-600 bg-green-100 text-green-900 ring-1 ring-green-600/30 font-medium'
        } else if (isUserPick) {
          className += 'border-2 border-red-600 bg-red-100 text-red-900 ring-1 ring-red-600/30'
        } else {
          className += 'border border-slate-200 bg-white text-slate-500'
        }

        return (
          <button
            key={letter}
            type="button"
            // data-answer-cell: opt out of the global button[disabled] dark
            // guard — these force-light cells were painted slate-800 once
            // answered, burying the CORRECT / YOUR ANSWER badges (founder
            // report 2026-07-06; same family as the drill submit flash).
            data-answer-cell
            disabled={phase === 'answered'}
            onClick={() => onPick(letter)}
            className={className}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-current text-xs font-semibold">
                {letter}
              </span>{' '}
              <MathText text={c} />
            </span>
            {phase === 'answered' && isCorrectChoice && (
              <span className="flex shrink-0 items-center gap-1 text-xs font-semibold uppercase tracking-wide text-green-700">
                <CheckCircle2 className="h-4 w-4" />
                Correct
              </span>
            )}
            {phase === 'answered' && isUserPick && !isCorrectChoice && (
              <span className="flex shrink-0 items-center gap-1 text-xs font-semibold uppercase tracking-wide text-red-700">
                <XCircle className="h-4 w-4" />
                Your answer
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

interface SprAnswerEntryProps {
  phase: 'idle' | 'answered'
  userAnswer: string | null
  isCorrect: boolean | null
  sprInput: string
  onSprInputChange: (s: string) => void
  /** Why the entry can't be graded, shown inline; null when there's nothing to say. */
  formatError: string | null
  onCommit: (answer: string) => void
}

/**
 * SPR (short-answer) entry. Numeric input + submit. After commit,
 * the input locks and shows the user's answer with correctness
 * coloring. Pressing Enter submits — matches the practice flow's
 * keyboard behavior.
 */
function SprAnswerEntry({
  phase,
  userAnswer,
  isCorrect,
  sprInput,
  onSprInputChange,
  formatError,
  onCommit,
}: SprAnswerEntryProps) {
  const hintId = useId()
  const errorId = useId()
  if (phase === 'answered') {
    // Locked, post-commit display. Color the value by correctness so
    // the user can see at a glance what they typed.
    const colorClass = isCorrect
      ? 'border-emerald-500/70 bg-emerald-50 text-emerald-900'
      : 'border-rose-500/70 bg-rose-50 text-rose-900'
    return (
      <div
        className={`rounded-md border px-3 py-2 text-sm font-medium ${colorClass}`}
        aria-label="Your answer"
      >
        Your answer: {userAnswer || '(skipped)'}
      </div>
    )
  }

  // Idle: input + submit button. Enter in the input also submits.
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        const trimmed = sprInput.trim()
        if (trimmed.length === 0) return
        onCommit(trimmed)
      }}
      className="flex flex-col gap-2"
    >
      <div className="flex gap-2">
        <input
          type="text"
          value={sprInput}
          onChange={(e) => onSprInputChange(e.target.value)}
          placeholder="Type your answer"
          autoComplete="off"
          // #48: "text" (not "numeric") so the mobile soft keyboard exposes
          // "/" and "." for the fraction/decimal formats the hint advertises,
          // matching the Runner's SPR input.
          inputMode="text"
          aria-describedby={formatError ? `${hintId} ${errorId}` : hintId}
          aria-invalid={formatError ? true : undefined}
          className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand focus:outline-none"
          aria-label="Your answer"
        />
        <Button
          type="submit"
          className="bg-brand text-on-brand"
          disabled={sprInput.trim().length === 0}
        >
          Submit
        </Button>
      </div>
      {/* #48: state the accepted numeric formats for screen readers + sighted users. */}
      <p id={hintId} className="text-xs text-muted-foreground">
        Enter a whole number, fraction (e.g. 3/4), or decimal (e.g. .75).
      </p>
      <SprEntryError id={errorId} message={formatError} />
    </form>
  )
}
